/**
 * Vincent B680 (Eric 2026-02-XX) — Custom hook `useShareLinks` extrait de
 * VideoDetailPage.js. Encapsule toute la logique de partage de vidéo :
 * état (modal, liens, expiry, feedback copié), fetch, création, révocation.
 *
 * Économise ~55 lignes dans VideoDetailPage.js.
 *
 * Usage :
 *     const share = useShareLinks({ videoId, api: API, lang });
 *     share.showShareModal, share.setShowShareModal, share.openShareModal,
 *     share.shareLinks, share.shareLoading, share.shareCopied,
 *     share.shareExpiry, share.setShareExpiry,
 *     share.createShareLink, share.copyShareLink, share.revokeShareLink
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

export default function useShareLinks({ videoId, api, lang = "fr" }) {
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLinks, setShareLinks] = useState([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareCopied, setShareCopied] = useState(null);
  const [shareExpiry, setShareExpiry] = useState(24);

  const loadShareLinks = async () => {
    try {
      const { data: res } = await axios.get(`${api}/api/videos/${videoId}/shares`, { withCredentials: true });
      setShareLinks(res.links || []);
    } catch (e) {
      console.warn("[Share] loadShareLinks failed:", e);
    }
  };

  const createShareLink = async () => {
    setShareLoading(true);
    try {
      const { data: res } = await axios.post(
        `${api}/api/videos/${videoId}/share`,
        { expires_hours: shareExpiry },
        { withCredentials: true },
      );
      const shareUrl = `${window.location.origin}/share/${res.token}`;
      await navigator.clipboard.writeText(shareUrl).catch(() => {});
      setShareCopied(res.token);
      setTimeout(() => setShareCopied(null), 2000);
      await loadShareLinks();
    } catch (e) {
      console.warn("[Share] createShareLink failed:", e);
      toast.error(lang === "fr" ? "Création du lien de partage impossible." : "Failed to create share link.");
    }
    setShareLoading(false);
  };

  const copyShareLink = async (token) => {
    const url = `${window.location.origin}/share/${token}`;
    await navigator.clipboard.writeText(url).catch(() => {});
    setShareCopied(token);
    setTimeout(() => setShareCopied(null), 2000);
  };

  const revokeShareLink = async (token) => {
    try {
      await axios.delete(`${api}/api/share/${token}`, { withCredentials: true });
      setShareLinks((prev) => prev.filter((l) => l.token !== token));
    } catch (e) {
      console.warn("[Share] revokeShareLink failed:", e);
      toast.error(lang === "fr" ? "Révocation du lien impossible." : "Failed to revoke share link.");
    }
  };

  const openShareModal = () => {
    setShowShareModal(true);
    loadShareLinks();
  };

  return {
    // state
    showShareModal,
    setShowShareModal,
    shareLinks,
    shareLoading,
    shareCopied,
    shareExpiry,
    setShareExpiry,
    // handlers
    openShareModal,
    createShareLink,
    copyShareLink,
    revokeShareLink,
    loadShareLinks,
  };
}
